import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Skeleton from '../../components/Skeleton';
import SectionHeader from '../../components/SectionHeader';
import { useSessions } from '../../hooks/useSessions';
import { useRidersById } from '../../hooks/useRiders';
import { HiOutlinePlusCircle } from 'react-icons/hi2';
import { SessionAnalysisType, SessionAnalysisTypeUtils } from '../../types/SessionAnalysisType';
import { SessionIcon } from '../../components/SessionIcon';
import { getSessionTypeName } from '../../utils/sessionIconUtils';
import SessionColorIndicator from '../../components/SessionColorIndicator';
import { compareSessionsDesc } from '../../utils/sessionSort';

export default function SessionList(): ReactElement {
  const { isPending: isSessionsPending, isFetching: isSessionsFetching, data: sessions } = useSessions();
  const { data: ridersById } = useRidersById();
  const [showHidden, setShowHidden] = useState(false);
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [riderFilter, setRiderFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Set page background color
  useEffect(() => {
    document.documentElement.style.setProperty('--page-bg-color', '#f3f4f6'); // gray-100
    return () => {
      document.documentElement.style.setProperty('--page-bg-color', 'transparent');
    };
  }, []);

  const entry = (label: string, value?: string) => {
    return (
      <div className="cr-text-base">
        <div className="cr-inline cr-break-after-auto cr-font-bold cr-uppercase">{label}:&nbsp; </div>
        <div className="cr-inline cr-font-normal cr-font-main">
          {value}
        </div>
      </div>
    );
  };

  const visibleSessions = useMemo(() => {
    // Singletons in the list: freeride is always shown (sorted by its date,
    // which tracks its newest run); discarded/hidden singletons appear behind
    // the "Show discarded" checkbox; reference calibrations never shows.
    const isListedSingleton = (session: any): boolean => {
      if (session.analysis_type === SessionAnalysisType.FREERIDE_UNSTRUCTURED) return true;
      if (!showDiscarded) return false;
      return SessionAnalysisTypeUtils.isDiscarded(session.analysis_type)
        || SessionAnalysisTypeUtils.isHidden(session.analysis_type);
    };
    const sessionsArray = Array.isArray(sessions) ? sessions : [];
    return sessionsArray
      .filter((session: any) => !SessionAnalysisTypeUtils.isSingletonLevel(session.analysis_type)
        || isListedSingleton(session))
      .filter((session: any) => showHidden || !session.hidden);
  }, [sessions, showHidden, showDiscarded]);

  // Dropdown options come from the currently visible sessions, so the lists
  // only ever offer choices that would show something.
  const riderOptions = useMemo(() => [...new Set(visibleSessions
    .map((session: any) => session.rider_id)
    .filter((riderId: any) => riderId != null))]
    .map((riderId: any) => ({ id: String(riderId), name: ridersById?.get(riderId)?.nickname ?? 'Unknown' }))
    .sort((a, b) => a.name.localeCompare(b.name)), [visibleSessions, ridersById]);
  const typeOptions = useMemo(() => [...new Set(visibleSessions
    .map((session: any) => session.analysis_type)
    .filter((analysisType: any) => analysisType != null))]
    .sort((a: any, b: any) => a - b)
    .map((analysisType: any) => ({ value: String(analysisType), name: getSessionTypeName(analysisType) })),
  [visibleSessions]);

  const grid = visibleSessions
    .filter((session: any) => riderFilter === 'all' || String(session.rider_id) === riderFilter)
    .filter((session: any) => typeFilter === 'all' || String(session.analysis_type) === typeFilter)
    ?.sort((a: any, b: any) => compareSessionsDesc(a, b, ridersById))
    .map((session: any) => (
      <Link
        to={`/sessions/${session.id}`}
        key={session.id}
        className={
          'cr-relative cr-flex cr-flex-col cr-gap-4 cr-rounded-2xl cr-bg-white cr-p-4 cr-text-cirrusBlue-400 ' +
          `cr-no-underline cr-shadow-md md:cr-pb-5 lg:cr-pb-20${session.hidden ? ' cr-opacity-60' : ''}`
        }
      >
        <div className="cr-flex cr-items-center cr-gap-3">
          <SessionIcon analysisType={session.analysis_type} size={32} className="cr-text-cirrusBlue-400" />
          <div className="font-montserrat cr-text-2xl cr-font-semibold">{session.date}</div>
        </div>
        {entry('Rider', ridersById?.get(session.rider_id)?.nickname)}
        {entry('Description', session.description)}
        <SessionColorIndicator sessionId={session.id} />
      </Link>
    ));
  return (
    <>
      <SectionHeader title="Sessions" isLoading={isSessionsFetching} />
      <div className="cr-mb-4 cr-flex cr-items-center cr-gap-6">
        <select
          value={riderFilter}
          onChange={(e) => setRiderFilter(e.target.value)}
          className="cr-cursor-pointer cr-rounded-md cr-border cr-border-gray-300
            cr-bg-white cr-px-3 cr-py-2 cr-font-main cr-text-sm cr-text-gray-700"
        >
          <option value="all">All riders</option>
          {riderOptions.map((rider) => (
            <option key={rider.id} value={rider.id}>{rider.name}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="cr-cursor-pointer cr-rounded-md cr-border cr-border-gray-300
            cr-bg-white cr-px-3 cr-py-2 cr-font-main cr-text-sm cr-text-gray-700"
        >
          <option value="all">All session types</option>
          {typeOptions.map((type) => (
            <option key={type.value} value={type.value}>{type.name}</option>
          ))}
        </select>
        <label className="cr-flex cr-w-fit cr-cursor-pointer cr-items-center cr-gap-2 cr-font-main cr-text-base">
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
            className="cr-cursor-pointer"
            style={{ accentColor: '#03365D', width: '18px', height: '18px' }}
          />
          Show hidden
        </label>
        <label className="cr-flex cr-w-fit cr-cursor-pointer cr-items-center cr-gap-2 cr-font-main cr-text-base">
          <input
            type="checkbox"
            checked={showDiscarded}
            onChange={(e) => setShowDiscarded(e.target.checked)}
            className="cr-cursor-pointer"
            style={{ accentColor: '#03365D', width: '18px', height: '18px' }}
          />
          Show discarded
        </label>
      </div>
      <Skeleton isPending={isSessionsPending}>
        <div className="cr-flex cr-flex-col cr-gap-6 md:cr-grid md:cr-grid-cols-4">
          <Link
            to={'/sessions/new'}
            key={'new'}
            className="font-montserrat cr-flex cr-flex-col cr-items-center cr-gap-4 cr-rounded-2xl cr-bg-white
                       cr-p-4 cr-text-center cr-text-xl cr-font-semibold cr-text-cirrusBlue-400 cr-no-underline cr-shadow-md md:cr-pb-5 lg:cr-p-5"
          >
            New Session
            <HiOutlinePlusCircle size={80} />
          </Link>
          {grid}
        </div>
      </Skeleton>
    </>
  );
}
